import { useState, useEffect, useRef, useCallback } from "react";
import { db } from "@/services/firebaseService";
import { getCampLocationsFromOrg } from "@/services/getCampLocationsFromOrg";
import { CampLocation } from "@/types/campLocation";
import { useFirestoreError } from "./useFirestoreError";
import { useAuth } from "@/context/AuthContext";
import { getLocationAccessGroupById } from "@/services/locationAccessGroupService";
import { isWelcomeRouteVisibleWorkflowStatus } from "@/utils/welcomeWorkflowStatus";
import { sortCampLocationsByName } from "@/utils/sortCampLocationsByName";

export interface UseLocationsByOrganizationOptions {
  /** When true, bypass location access group filtering and show all locations for the org. Use for admin contexts (e.g. org edit page). */
  skipLocationAccessGroupFilter?: boolean;
  /**
   * When set (including `null`), overrides `userProfile.locationAccessGroupId` for which locations load.
   * Use for invitation-driven flows: pass the invitation's group, or `null` for full-org access for that link.
   * When omitted, behavior is unchanged (uses signed-in user's profile group).
   */
  locationAccessGroupId?: string | null;
  /**
   * When true (e.g. /welcome location editing), only include locations whose `workflowStatus` is
   * `wf_external*` or `wf_internal30`. Results are sorted by location `name` (same order as the
   * Switch location UI); initial selection is the first in that list unless the parent overrides
   * (e.g. invitation `defaultLocationId`).
   */
  restrictToWelcomeWorkflowStatuses?: boolean;
}

export const useLocationsByOrganization = (
  organizationId: string | null,
  options?: UseLocationsByOrganizationOptions
) => {
  const { userProfile } = useAuth();
  const skipGroupFilter = options?.skipLocationAccessGroupFilter ?? false;
  /** When undefined, fall back to userProfile; when null or string, caller controls scope */
  const groupIdOverride = options?.locationAccessGroupId;
  const welcomeWorkflowOnly = options?.restrictToWelcomeWorkflowStatuses ?? false;
  const [locations, setLocations] = useState<CampLocation[]>([]);
  const [currentLocation, setCurrentLocation] = useState<CampLocation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetchCompleted, setIsFetchCompleted] = useState(false);
  const { handleFirestoreError } = useFirestoreError();
  
  // Add refs to track fetch state and prevent unnecessary re-fetches
  const hasLoggedNoLocations = useRef(false);
  const hasFetchedLocations = useRef(false);
  // Keep track of the current organizationId to detect changes
  const prevOrgIdRef = useRef<string | null>(organizationId);
  const prevGroupIdRef = useRef<string | null>(null);
  // For component mount tracking
  const isMountedRef = useRef(false);
  // For fetch in progress tracking
  const fetchingInProgressRef = useRef(false);
  const prevWelcomeWorkflowFilterRef = useRef<boolean | undefined>(undefined);

  const fetchLocations = useCallback(async (forceRefresh: boolean = false) => {
    if (!organizationId) {
      setIsLoading(false);
      setLocations([]);
      setCurrentLocation(null);
      setIsFetchCompleted(true);
      return;
    }
    
    // Prevent concurrent fetches
    if (fetchingInProgressRef.current && !forceRefresh) {
      return;
    }
    
    // Skip if we've already fetched and nothing has changed, unless force refresh
    const resolvedGroupId = skipGroupFilter
      ? null
      : (groupIdOverride !== undefined
          ? groupIdOverride
          : (userProfile?.locationAccessGroupId || null));
    if (hasFetchedLocations.current &&
        organizationId === prevOrgIdRef.current &&
        resolvedGroupId === prevGroupIdRef.current &&
        welcomeWorkflowOnly === prevWelcomeWorkflowFilterRef.current &&
        !forceRefresh) {
      return;
    }
    
    try {
      fetchingInProgressRef.current = true;
      setIsLoading(true);

      const groupId = skipGroupFilter
        ? null
        : (groupIdOverride !== undefined
            ? groupIdOverride
            : (userProfile?.locationAccessGroupId || null));
      let locationsData: CampLocation[] = [];
      if (groupId) {
        const group = await getLocationAccessGroupById(groupId);
        if (group && group.organizationId === organizationId) {
          locationsData = await getCampLocationsFromOrg(db, organizationId, { locationIds: group.locationIds || [] });
        } else {
          // Fail-closed: if group missing/mismatched, show no locations.
          locationsData = [];
        }
      } else {
        locationsData = await getCampLocationsFromOrg(db, organizationId);
      }

      if (welcomeWorkflowOnly) {
        locationsData = locationsData.filter((loc) =>
          isWelcomeRouteVisibleWorkflowStatus(loc.workflowStatus)
        );
        locationsData = sortCampLocationsByName(locationsData);
      }
      
      // Update tracking refs
      prevOrgIdRef.current = organizationId;
      prevGroupIdRef.current = groupId;
      prevWelcomeWorkflowFilterRef.current = welcomeWorkflowOnly;
      hasFetchedLocations.current = true;
      
      // Handle no locations case
      if (!locationsData || locationsData.length === 0) {
        if (!hasLoggedNoLocations.current) {
          hasLoggedNoLocations.current = true;
        }
        setLocations([]);
        setCurrentLocation(null);
      } else {
        setLocations(locationsData);
        
        // If we already had a current location, try to find it in the new data
        if (currentLocation) {
          const updatedCurrentLocation = locationsData.find(loc => loc.id === currentLocation.id);
          if (updatedCurrentLocation) {
            setCurrentLocation(updatedCurrentLocation);
          } else {
            // If the current location is no longer available, select the first one
            setCurrentLocation(locationsData[0]);
          }
        } else {
          // Set the first location as current if none was selected before
          setCurrentLocation(locationsData[0]);
        }
      }
    } catch (error) {
      console.error("useLocationsByOrganization: Error fetching locations:", error);
      handleFirestoreError(error);
    } finally {
      setIsLoading(false);
      setIsFetchCompleted(true);
      fetchingInProgressRef.current = false;
    }
  }, [organizationId, currentLocation, handleFirestoreError, userProfile?.locationAccessGroupId, skipGroupFilter, groupIdOverride, welcomeWorkflowOnly]);
  
  // Function to handle location selection
  const selectLocation = useCallback((location: CampLocation) => {
    setCurrentLocation(location);
  }, []);
  
  // Initial fetch on mount or when organizationId changes
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
    }
    
    if (organizationId !== prevOrgIdRef.current) {
      hasFetchedLocations.current = false;
      hasLoggedNoLocations.current = false;
    }

    const resolvedGroupId = skipGroupFilter
      ? null
      : (groupIdOverride !== undefined
          ? groupIdOverride
          : (userProfile?.locationAccessGroupId || null));
    if (resolvedGroupId !== prevGroupIdRef.current) {
      hasFetchedLocations.current = false;
      hasLoggedNoLocations.current = false;
    }

    if (welcomeWorkflowOnly !== prevWelcomeWorkflowFilterRef.current) {
      hasFetchedLocations.current = false;
      hasLoggedNoLocations.current = false;
    }

    fetchLocations();
  }, [organizationId, fetchLocations, userProfile?.locationAccessGroupId, skipGroupFilter, groupIdOverride, welcomeWorkflowOnly]);
  
  return { 
    locations, 
    currentLocation, 
    selectLocation, 
    isLoading, 
    isFetchCompleted,
    refreshLocations: () => fetchLocations(true)
  };
};
